import { useState, useEffect, useCallback, useRef } from 'react';
import { publicQuery, mutate } from '../graphql/client';
import {
  GET_COMMIT,
  GET_USER_COMMITS,
  CREATE_COMMIT,
  UPDATE_COMMIT,
  DELETE_COMMIT,
  ADD_REACTION,
  REMOVE_REACTION,
  GENERATE_LINKEDIN_POST,
} from '../graphql/operations';
import { getCached, setCache, getCommitCacheKey, getUserCommitsCacheKey, invalidateCache } from '../utils/cache';
import type {
  Commit,
  CommitConnection,
  CreateCommitInput,
  UpdateCommitInput,
  ReactionType,
  LinkedInPost,
} from '../types/api';

interface UseCommitOptions {
  initialData?: Commit | null;
}

interface UseCommitResult {
  commit: Commit | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCommit(id: string, options?: UseCommitOptions): UseCommitResult {
  // Priority: initialData > cache > null
  const cacheKey = getCommitCacheKey(id);
  const cached = getCached<Commit>(cacheKey);
  const initialCommit = options?.initialData || cached;

  const [commit, setCommit] = useState<Commit | null>(initialCommit);
  // If we have initial data, start with loading false (show data immediately)
  const [loading, setLoading] = useState(!initialCommit);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Use ref to track if we have data (avoids dependency on commit state)
  const hasDataRef = useRef(!!initialCommit);

  const fetchCommit = useCallback(async (showLoading = true) => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Only show loading if we don't have any data yet
    if (showLoading && !hasDataRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      // Use publicQuery so unauthenticated users can view public commits
      const data = await publicQuery<{ getCommit: Commit | null }>(GET_COMMIT, { id });
      setCommit(data.getCommit);
      hasDataRef.current = !!data.getCommit;
      // Cache the result
      if (data.getCommit) {
        setCache(cacheKey, data.getCommit);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch commit'));
    } finally {
      setLoading(false);
    }
  }, [id, cacheKey]);

  useEffect(() => {
    // Only fetch once per id
    if (!hasFetched) {
      setHasFetched(true);
      fetchCommit(!initialCommit);
    }
  }, [hasFetched, fetchCommit, initialCommit]);

  // Reset hasFetched when id changes
  useEffect(() => {
    setHasFetched(false);
    hasDataRef.current = false;
  }, [id]);

  const refetch = useCallback(() => fetchCommit(false), [fetchCommit]);

  return { commit, loading, error, refetch };
}

interface UseUserCommitsOptions {
  branch?: string | null;
  sortDirection?: 'asc' | 'desc';
}

interface UseUserCommitsResult {
  commits: Commit[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  setCommits: React.Dispatch<React.SetStateAction<Commit[]>>;
}

export function useUserCommits(userId: string, limit = 10, options?: UseUserCommitsOptions): UseUserCommitsResult {
  const branch = options?.branch;
  const sortDirection = options?.sortDirection || 'desc';

  // Include branch and sortDirection in cache key for proper caching
  const cacheKey = branch
    ? `${getUserCommitsCacheKey(userId)}:${branch}:${sortDirection}`
    : `${getUserCommitsCacheKey(userId)}:${sortDirection}`;
  const cached = getCached<Commit[]>(cacheKey);

  const [commits, setCommits] = useState<Commit[]>(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  // Use ref to track if we have data (avoids dependency on commits state)
  const hasDataRef = useRef(!!cached?.length);
  // Track request ID to ignore stale responses when switching branches quickly
  const requestIdRef = useRef(0);

  const fetchCommits = useCallback(async (token?: string, showLoading = true) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Increment request ID for this fetch
    const currentRequestId = ++requestIdRef.current;

    // Show loading state when requested (UI shows skeleton for initial load, spinner for load more)
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      // Use publicQuery so profile pages work without authentication
      const data = await publicQuery<{ getUserCommits: CommitConnection }>(GET_USER_COMMITS, {
        userId,
        limit,
        nextToken: token,
        sortDirection,
        branch: branch || undefined,
      });

      // Ignore stale responses - only apply if this is still the latest request
      if (requestIdRef.current !== currentRequestId) {
        return;
      }

      if (token) {
        setCommits((prev) => [...prev, ...data.getUserCommits.items]);
      } else {
        setCommits(data.getUserCommits.items);
        hasDataRef.current = data.getUserCommits.items.length > 0;
        // Cache the initial page
        setCache(cacheKey, data.getUserCommits.items);
      }
      setNextToken(data.getUserCommits.nextToken || undefined);
    } catch (err) {
      // Ignore errors from stale requests
      if (requestIdRef.current !== currentRequestId) {
        return;
      }
      setError(err instanceof Error ? err : new Error('Failed to fetch commits'));
    } finally {
      // Only clear loading if this is still the latest request
      if (requestIdRef.current === currentRequestId) {
        setLoading(false);
      }
    }
  }, [userId, limit, cacheKey, sortDirection, branch]);

  useEffect(() => {
    // Only fetch once per userId/branch combination
    if (!hasFetched && userId) {
      setHasFetched(true);
      fetchCommits(undefined, !cached);
    }
  }, [hasFetched, userId, fetchCommits, cached]);

  // Reset when userId, branch, or sortDirection changes - set loading true to show skeleton immediately
  useEffect(() => {
    setHasFetched(false);
    hasDataRef.current = false;
    setCommits([]);
    setNextToken(undefined);
    setLoading(true);
  }, [userId, branch, sortDirection]);

  const loadMore = useCallback(async () => {
    if (nextToken) {
      await fetchCommits(nextToken, true);
    }
  }, [nextToken, fetchCommits]);

  const refetch = useCallback(async () => {
    setNextToken(undefined);
    await fetchCommits(undefined, false);
  }, [fetchCommits]);

  return {
    commits,
    loading,
    error,
    hasMore: !!nextToken,
    loadMore,
    refetch,
    setCommits,
  };
}

interface UseCommitMutationsResult {
  createCommit: (input: CreateCommitInput) => Promise<Commit>;
  updateCommit: (id: string, input: UpdateCommitInput) => Promise<Commit>;
  deleteCommit: (id: string) => Promise<boolean>;
  addReaction: (commitId: string, type: ReactionType) => Promise<void>;
  removeReaction: (commitId: string, type: ReactionType) => Promise<void>;
  generateLinkedInPost: (commitId: string) => Promise<LinkedInPost>;
  loading: boolean;
}

export function useCommitMutations(): UseCommitMutationsResult {
  const [loading, setLoading] = useState(false);

  const createCommit = useCallback(async (input: CreateCommitInput): Promise<Commit> => {
    setLoading(true);
    try {
      const data = await mutate<{ createCommit: Commit }>(CREATE_COMMIT, { input });
      // Invalidate feed caches so new commit appears
      invalidateCache('feed:');
      // Invalidate 'me' cache so new branches appear in BranchesModal
      invalidateCache('me');
      return data.createCommit;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCommit = useCallback(async (id: string, input: UpdateCommitInput): Promise<Commit> => {
    setLoading(true);
    try {
      const data = await mutate<{ updateCommit: Commit }>(UPDATE_COMMIT, { id, input });
      // Invalidate specific commit cache and feed caches
      invalidateCache(`commit:${id}`);
      invalidateCache('feed:');
      return data.updateCommit;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCommit = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      const data = await mutate<{ deleteCommit: boolean }>(DELETE_COMMIT, { id });
      // Invalidate specific commit cache and all feed caches
      invalidateCache(`commit:${id}`);
      invalidateCache('feed:');
      invalidateCache('userCommits:');
      return data.deleteCommit;
    } finally {
      setLoading(false);
    }
  }, []);

  const addReaction = useCallback(async (commitId: string, type: ReactionType): Promise<void> => {
    await mutate(ADD_REACTION, { commitId, type });
  }, []);

  const removeReaction = useCallback(async (commitId: string, type: ReactionType): Promise<void> => {
    await mutate(REMOVE_REACTION, { commitId, type });
  }, []);

  const generateLinkedInPost = useCallback(async (commitId: string): Promise<LinkedInPost> => {
    setLoading(true);
    try {
      const data = await mutate<{ generateLinkedInPost: LinkedInPost }>(GENERATE_LINKEDIN_POST, { commitId });
      return data.generateLinkedInPost;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createCommit,
    updateCommit,
    deleteCommit,
    addReaction,
    removeReaction,
    generateLinkedInPost,
    loading,
  };
}
